import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/base/Pagination";
import usePageMeta from "@/hooks/usePageMeta";
import {
  PAGE_SIZE,
  filterMovies,
  getCategoryBySlug,
  getMoviesByCategory,
  paginate,
  totalPages,
} from "@/utils/catalog";
import { eraOptions, genreOptions, regionOptions, sortOptions } from "@/mocks/categories";

export default function CategoryPage() {
  const { type } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const category = getCategoryBySlug(type);

  const [region, setRegion] = useState("全部地区");
  const [era, setEra] = useState("全部年代");
  const [genre, setGenre] = useState("全部题材");
  const [sort, setSort] = useState("latest");
  const [filterOpen, setFilterOpen] = useState(false);

  const pageParam = Number(searchParams.get("page") ?? "1");
  const page = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page]);

  useEffect(() => {
    setRegion("全部地区");
    setEra("全部年代");
    setGenre("全部题材");
    setSort("latest");
    setFilterOpen(false);
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  usePageMeta(
    category ? `${category.name} - 黄色片免费看` : "分类不存在 - 黄色片免费看",
    category
      ? `${category.name}视频免费观看与资源分享，收录${category.intro}`
      : "该分类暂不存在，请返回首页浏览其它影片分类。",
  );

  const movies = useMemo(
    () => (category ? getMoviesByCategory(category.slug) : []),
    [category],
  );

  const filtered = useMemo(
    () => filterMovies(movies, { region, era, genre, sort }),
    [movies, region, era, genre, sort],
  );

  const pageCount = totalPages(filtered.length, PAGE_SIZE);
  const current = paginate(filtered, Math.min(page, pageCount), PAGE_SIZE);
  const genres = genreOptions[category?.slug ?? "movie"] ?? ["全部题材"];

  const changePage = (next: number) => {
    setSearchParams(next > 1 ? { page: String(next) } : {});
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetFilters = () => {
    setRegion("全部地区");
    setEra("全部年代");
    setGenre("全部题材");
    setSort("latest");
    setSearchParams({});
  };

  const hasFilter =
    region !== "全部地区" || era !== "全部年代" || genre !== "全部题材" || sort !== "latest";

  if (!category) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 items-center justify-center px-4 py-20">
          <div className="w-full max-w-md rounded-lg border border-background-200 bg-background-100/60 p-8 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-50 text-foreground-400">
              <i className="ri-folder-warning-line text-2xl" />
            </span>
            <h1 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
              该分类不存在
            </h1>
            <p className="mt-2 text-sm text-foreground-500">
              链接中的分类可能已被调整，去首页看看其它分类吧。
            </p>
            <Link
              to="/"
              className="mt-5 inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2.5 text-sm text-background-50 hover:bg-primary-600"
            >
              <i className="ri-home-4-line" /> 返回首页
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-8 md:py-8">
          <nav className="flex items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="hover:text-primary-600">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">{category.name}</span>
          </nav>

          <header className="mt-4 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100/60 p-5 md:flex-row md:items-center md:justify-between md:p-6">
            <div className="flex items-start gap-3.5">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                <i className={`${category.icon} text-2xl`} />
              </span>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">
                    {category.name}
                  </h1>
                  <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-800">
                    {category.tagline}
                  </span>
                </div>
                <p className="mt-2 max-w-[720px] text-sm leading-relaxed text-foreground-600">
                  {category.intro}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs text-foreground-500 md:flex-col md:items-end md:gap-1">
              <span className="flex items-center gap-1">
                <i className="ri-stack-line text-base text-primary-500" /> 共 {movies.length} 部
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-refresh-line text-base text-primary-500" /> 持续整理中
              </span>
            </div>
          </header>

          <section className="mt-5 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <div className="flex items-center justify-between lg:hidden">
              <span className="text-sm font-medium text-foreground-800">筛选与排序</span>
              <button
                type="button"
                onClick={() => setFilterOpen((prev) => !prev)}
                className="flex items-center gap-1 rounded-md border border-background-300 px-3 py-1.5 text-xs text-foreground-700"
              >
                <i className="ri-filter-3-line" /> {filterOpen ? "收起" : "展开"}
              </button>
            </div>

            <div className={`${filterOpen ? "block" : "hidden"} lg:block`}>
              <div className="mt-3 flex items-start gap-2.5 lg:mt-0">
                <span className="mt-1.5 hidden w-12 shrink-0 text-xs text-foreground-500 lg:block">
                  题材
                </span>
                <div className="no-scrollbar flex flex-1 gap-1.5 overflow-x-auto pb-1">
                  {genres.map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => {
                        setGenre(item);
                        setSearchParams({});
                      }}
                      className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-xs transition-colors ${
                        genre === item
                          ? "border-primary-500 bg-primary-500 text-background-50"
                          : "border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600"
                      }`}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-3 border-t border-background-200 pt-4 sm:flex-row sm:flex-wrap sm:items-center">
                <div className="flex min-w-0 flex-1 flex-wrap gap-2">
                  <select
                    value={region}
                    onChange={(event) => {
                      setRegion(event.target.value);
                      setSearchParams({});
                    }}
                    aria-label="按地区筛选"
                    className="min-w-0 flex-1 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs text-foreground-700 outline-none sm:flex-none sm:w-[130px]"
                  >
                    {regionOptions.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                  <select
                    value={era}
                    onChange={(event) => {
                      setEra(event.target.value);
                      setSearchParams({});
                    }}
                    aria-label="按年代筛选"
                    className="min-w-0 flex-1 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs text-foreground-700 outline-none sm:flex-none sm:w-[130px]"
                  >
                    {eraOptions.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                  <select
                    value={sort}
                    onChange={(event) => setSort(event.target.value)}
                    aria-label="排序方式"
                    className="min-w-0 flex-1 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-xs text-foreground-700 outline-none sm:flex-none sm:w-[130px]"
                  >
                    {sortOptions.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs text-foreground-500">
                    筛选结果 <span className="text-primary-600">{filtered.length}</span> 部
                  </span>
                  <button
                    type="button"
                    onClick={resetFilters}
                    disabled={!hasFilter}
                    className={`flex items-center gap-1 whitespace-nowrap rounded-md border px-3 py-2 text-xs transition-colors ${
                      hasFilter
                        ? "border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600"
                        : "cursor-not-allowed border-background-200 text-foreground-300"
                    }`}
                  >
                    <i className="ri-restart-line" /> 重置筛选
                  </button>
                </div>
              </div>
            </div>
          </section>

          {current.length ? (
            <>
              <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 md:gap-4">
                {current.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
              <Pagination page={Math.min(page, pageCount)} total={pageCount} onChange={changePage} />
            </>
          ) : (
            <div className="mt-5 flex flex-col items-center rounded-lg border border-background-200 bg-background-100/60 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-50 text-foreground-400">
                <i className="ri-film-line text-2xl" />
              </span>
              <h2 className="mt-4 font-heading text-base font-semibold text-foreground-950">
                没有符合筛选条件的影片
              </h2>
              <p className="mt-2 max-w-sm text-sm text-foreground-500">
                试着放宽地区或年代的限制，也可以重置筛选查看该分类下的全部内容。
              </p>
              <div className="mt-5 flex flex-wrap items-center justify-center gap-2.5">
                <button
                  type="button"
                  onClick={resetFilters}
                  className="rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  重置筛选条件
                </button>
                <Link
                  to="/"
                  className="rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-600"
                >
                  返回首页
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}